import type {
    IGatewayCredentialIssueResponse,
    IGatewayCredentialResponse,
    IGatewayResponse,
} from '@escloud/types';
import { ChevronDown, ChevronRight, MoreHorizontal, Plus, Server, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { ConfirmationDialog } from '@/components/confirmation-dialog';
import { TokenShownOnceModal } from '@/components/gateways/token-shown-once-modal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useCurrentTenant } from '@/context/tenant-registry-context';
import {
    useCreateGateway,
    useDeleteGateway,
    useGatewayCredentials,
    useGateways,
    useIssueCredential,
    useRevokeCredential,
    useUpdateGateway,
} from '@/hooks/use-gateways';

interface PendingToken {
    token: string;
}

export function GatewaysPage() {
    const { tenantId } = useParams<{ tenantId: string }>();
    const { entry: currentTenant, isConfirmed } = useCurrentTenant();
    const { data, isLoading } = useGateways(tenantId);

    // Confirmed by a regional API, never by a token claim: a claim keeps a
    // demoted role for the rest of the session.
    const isAdmin = isConfirmed && (currentTenant?.role === 'admin' || currentTenant?.role === 'owner');

    // Single token-display modal owned at the page level; nested
    // components surface a newly-issued token by calling setTokenState.
    const [tokenState, setTokenState] = useState<PendingToken | null>(null);
    const [expandedGatewayId, setExpandedGatewayId] = useState<string | null>(null);

    if (isLoading) return <GatewaysSkeleton />;

    const gateways = data?.items ?? [];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Gateways</h1>
                    <p className="text-muted-foreground">
                        Manage on-prem DICOM gateways and their access credentials
                    </p>
                </div>
                {isAdmin && (
                    <AddGatewayDialog
                        tenantId={tenantId!}
                        onIssued={(c) => setTokenState({ token: c.token })}
                    />
                )}
            </div>

            <Card>
                <CardContent className="p-0">
                    {gateways.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-8"></TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Last seen</TableHead>
                                    {isAdmin && <TableHead className="w-12"></TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {gateways.map((gw) => (
                                    <GatewayRow
                                        key={gw.id}
                                        gateway={gw}
                                        tenantId={tenantId!}
                                        expanded={expandedGatewayId === gw.id}
                                        onToggle={() =>
                                            setExpandedGatewayId(expandedGatewayId === gw.id ? null : gw.id)
                                        }
                                        isAdmin={isAdmin}
                                        onTokenIssued={(c) => setTokenState({ token: c.token })}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {tokenState && (
                <TokenShownOnceModal token={tokenState.token} open onClose={() => setTokenState(null)} />
            )}
        </div>
    );
}

interface GatewayRowProps {
    gateway: IGatewayResponse;
    tenantId: string;
    expanded: boolean;
    onToggle: () => void;
    isAdmin: boolean;
    onTokenIssued: (credential: IGatewayCredentialIssueResponse) => void;
}

function GatewayRow({ gateway, tenantId, expanded, onToggle, isAdmin, onTokenIssued }: GatewayRowProps) {
    const updateGateway = useUpdateGateway(tenantId);
    const deleteGateway = useDeleteGateway(tenantId);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const handleToggleDisabled = () => {
        updateGateway.mutate(
            { gatewayId: gateway.id, data: { disabled: !gateway.disabled } },
            {
                onSuccess: () => toast.success(gateway.disabled ? 'Gateway enabled' : 'Gateway disabled'),
                onError: (e) => toast.error(e.message || 'Update failed'),
            }
        );
    };

    const handleConfirmDelete = () => {
        deleteGateway.mutate(gateway.id, {
            onSuccess: () => {
                toast.success('Gateway deleted');
                setConfirmDeleteOpen(false);
            },
            onError: (e) => toast.error(e.message || 'Delete failed'),
        });
    };

    return (
        <>
            <TableRow>
                <TableCell className="p-0 pl-2">
                    <Button variant="ghost" size="icon" onClick={onToggle} aria-label="Expand">
                        {expanded ? (
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                            <ChevronRight className="h-4 w-4" />
                        )}
                    </Button>
                </TableCell>
                <TableCell>
                    <div>
                        <p className="font-medium">{gateway.name}</p>
                        {gateway.description && (
                            <p className="text-sm text-muted-foreground">{gateway.description}</p>
                        )}
                    </div>
                </TableCell>
                <TableCell>
                    {gateway.disabled ? (
                        <Badge variant="secondary">Disabled</Badge>
                    ) : (
                        <Badge variant="default">Active</Badge>
                    )}
                </TableCell>
                <TableCell>
                    <span className="text-sm text-muted-foreground">
                        {formatRelative(gateway.lastSeenAt)}
                    </span>
                </TableCell>
                {isAdmin && (
                    <TableCell>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={handleToggleDisabled}>
                                    {gateway.disabled ? 'Enable' : 'Disable'}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() => setConfirmDeleteOpen(true)}
                                >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </TableCell>
                )}
            </TableRow>
            {expanded && (
                <TableRow>
                    <TableCell colSpan={isAdmin ? 5 : 4} className="bg-muted/30 p-4">
                        <GatewayCredentialList
                            tenantId={tenantId}
                            gatewayId={gateway.id}
                            isAdmin={isAdmin}
                            onTokenIssued={onTokenIssued}
                        />
                    </TableCell>
                </TableRow>
            )}
            <ConfirmationDialog
                open={confirmDeleteOpen}
                onOpenChange={setConfirmDeleteOpen}
                title={`Delete gateway "${gateway.name}"?`}
                description="Active credentials will be revoked immediately. This cannot be undone."
                confirmLabel="Delete"
                variant="destructive"
                onConfirm={handleConfirmDelete}
                isPending={deleteGateway.isPending}
            />
        </>
    );
}

function GatewayCredentialList({
    tenantId,
    gatewayId,
    isAdmin,
    onTokenIssued,
}: {
    tenantId: string;
    gatewayId: string;
    isAdmin: boolean;
    onTokenIssued: (credential: IGatewayCredentialIssueResponse) => void;
}) {
    const { data, isLoading } = useGatewayCredentials(tenantId, gatewayId);
    const issueCredential = useIssueCredential(tenantId, gatewayId);
    const revokeCredential = useRevokeCredential(tenantId, gatewayId);
    const [revokeTarget, setRevokeTarget] = useState<IGatewayCredentialResponse | null>(null);

    const credentials = data?.items ?? [];
    const activeCount = credentials.filter((c) => isCredentialActive(c)).length;
    const atMax = activeCount >= 2;

    const handleIssue = () => {
        issueCredential.mutate(undefined, {
            onSuccess: (cred) => {
                onTokenIssued(cred);
                toast.success('Credential issued');
            },
            onError: (e) => toast.error(e.message || 'Failed to issue credential'),
        });
    };

    const handleConfirmRevoke = () => {
        if (!revokeTarget) return;
        revokeCredential.mutate(revokeTarget.id, {
            onSuccess: () => {
                toast.success('Credential revoked');
                setRevokeTarget(null);
            },
            onError: (e) => toast.error(e.message || 'Failed to revoke'),
        });
    };

    if (isLoading) return <Skeleton className="h-20 w-full" />;

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">
                    Credentials ({activeCount} active / {credentials.length} total)
                </h3>
                {isAdmin && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={handleIssue}
                        disabled={atMax || issueCredential.isPending}
                        title={atMax ? 'Max 2 active credentials — revoke one first' : undefined}
                    >
                        <Plus className="mr-1 h-4 w-4" />
                        Issue credential
                    </Button>
                )}
            </div>
            {credentials.length === 0 ? (
                <p className="text-sm text-muted-foreground">No credentials yet.</p>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Token</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Valid until</TableHead>
                            <TableHead>Last used</TableHead>
                            {isAdmin && <TableHead className="w-12"></TableHead>}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {credentials.map((c) => (
                            <TableRow key={c.id}>
                                <TableCell>
                                    <code className="text-xs">{c.tokenPrefix}…</code>
                                </TableCell>
                                <TableCell>
                                    <CredentialStatusBadge credential={c} />
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                    {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : 'Never'}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                    {formatRelative(c.lastUsedAt)}
                                </TableCell>
                                {isAdmin && (
                                    <TableCell>
                                        {isCredentialActive(c) && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setRevokeTarget(c)}
                                            >
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        )}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            <ConfirmationDialog
                open={revokeTarget !== null}
                onOpenChange={(next) => {
                    if (!next) setRevokeTarget(null);
                }}
                title="Revoke credential?"
                description={
                    revokeTarget
                        ? `Token ${revokeTarget.tokenPrefix}… will stop working immediately. Any gateway using it will lose access.`
                        : ''
                }
                confirmLabel="Revoke"
                variant="destructive"
                onConfirm={handleConfirmRevoke}
                isPending={revokeCredential.isPending}
            />
        </div>
    );
}

function CredentialStatusBadge({ credential }: { credential: IGatewayCredentialResponse }) {
    if (credential.revokedAt) return <Badge variant="secondary">Revoked</Badge>;
    if (credential.expiresAt && new Date(credential.expiresAt) <= new Date()) {
        return <Badge variant="secondary">Expired</Badge>;
    }
    return <Badge variant="default">Active</Badge>;
}

function isCredentialActive(c: IGatewayCredentialResponse): boolean {
    if (c.revokedAt) return false;
    if (c.expiresAt && new Date(c.expiresAt) <= new Date()) return false;
    return true;
}

function AddGatewayDialog({
    tenantId,
    onIssued,
}: {
    tenantId: string;
    onIssued: (credential: IGatewayCredentialIssueResponse) => void;
}) {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const createGateway = useCreateGateway(tenantId);

    const reset = () => {
        setName('');
        setDescription('');
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            toast.error('Name is required');
            return;
        }
        createGateway.mutate(
            { name: name.trim(), description: description.trim() || undefined },
            {
                onSuccess: (result) => {
                    onIssued(result.credential);
                    toast.success('Gateway created');
                    setOpen(false);
                    reset();
                },
                onError: (e) => toast.error(e.message || 'Failed to create gateway'),
            }
        );
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (!next) reset();
            }}
        >
            <DialogTrigger asChild>
                <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Gateway
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Add Gateway</DialogTitle>
                    <DialogDescription>
                        Register a new on-prem gateway. An initial bearer-token credential will be issued —
                        make sure to save it.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label htmlFor="gw-name">Name</Label>
                            <Input
                                id="gw-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Hospital A — Imaging Bay"
                                maxLength={255}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="gw-desc">Description (optional)</Label>
                            <Input
                                id="gw-desc"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                maxLength={500}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={createGateway.isPending}>
                            {createGateway.isPending ? 'Creating…' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function EmptyState() {
    return (
        <div className="flex flex-col items-center py-12 text-center">
            <Server className="h-12 w-12 text-muted-foreground" />
            <p className="mt-4 font-medium">No gateways yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
                Add a gateway to receive DICOM pushes from your on-prem environment.
            </p>
        </div>
    );
}

function GatewaysSkeleton() {
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <Skeleton className="h-8 w-32" />
                    <Skeleton className="mt-2 h-4 w-72" />
                </div>
                <Skeleton className="h-10 w-32" />
            </div>
            <Card>
                <CardContent className="p-0">
                    <div className="space-y-4 p-4">
                        {[1, 2].map((i) => (
                            <Skeleton key={i} className="h-12 w-full" />
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

/**
 * Returns a relative time string like "5 minutes ago", "2 days ago",
 * or "Never" for null. Avoids adding date-fns as a dep for one helper.
 */
function formatRelative(iso: string | null): string {
    if (!iso) return 'Never';
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs < 0) return new Date(iso).toLocaleString();
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
    return new Date(iso).toLocaleDateString();
}
